import {RequestData} from "@web-libs/core/http/infrastructure/shared/build-fetch-function";
import {withDeep} from "@web-libs/util/with/with";
import {MutableRefObject} from "react";
import {AuthConcern} from "@web-libs/features/auth/main/application/use-auth.concern";

type RefreshFunction = () => Promise<string | null>
type LogoutFunction = () => void

export const createRefreshInterceptor = (authConcern: AuthConcern) => {
    let refreshState = new RefreshState()

    const startRefresh = async (response: Response, requestData: RequestData): Promise<Response> => {
        refreshState.startRefreshing()
        try {
            const {
                token,
                response: newResponse
            } = await refreshAndRetry(authConcern, authConcern.logout, requestData, response)
            refreshState.stopRefreshing(token)
            return newResponse
        } catch (e) {
            refreshState.stopRefreshing(null)
            authConcern.logout()
            return response
        }
    }

    const retryWhenRefreshFinished = async (requestData: RequestData, originalResposne:Response): Promise<Response> => {
        return await refreshState.waitForRefresh(async (authToken) => {
            if(!authToken){
                return originalResposne
            }
            return await retryRequest(requestData, authToken, authConcern.logout)
        })
    }

    const scheduleRefresh = async (response: Response, requestData: RequestData): Promise<Response> => {
        if (refreshState.isRefreshing) {
            return await retryWhenRefreshFinished(requestData, response)
        }
        return await startRefresh(response, requestData)
    }

    return async (response: Response, requestData: RequestData): Promise<Response> => {
        if (response.status === 401) {
            return await scheduleRefresh(response, requestData)
        }
        return response
    };
}

export const refreshAndRetry = async (
    authConcern: AuthConcern,
    logout: LogoutFunction,
    requestData: RequestData,
    originalResponse: Response
): Promise<{ token: string | null, response: Response }> => {
    const token = await authConcern.refreshToken()
    if (!token) {
        logout();
        return {token: null, response: originalResponse}
    }
    return {
        token,
        response: await retryRequest(requestData, token, logout)
    }
}

const retryRequest = async (
    requestData: RequestData,
    token: string,
    logout: LogoutFunction
): Promise<Response> => {
    const originalRequestInit = requestData.requestInit
    const newAuthHeader = {Authorization: `Bearer ${token}`}
    const newRequestInit = withDeep(originalRequestInit, "headers", newAuthHeader)
    const response = await requestData.retry(requestData.url, newRequestInit)
    if (response.status === 401) {
        logout()
    }
    return response
}

type WaitingForRefreshFunction = (authToken: string | null) => Promise<Response>
type WaitingForRefreshItem = {
    waitingFunction: WaitingForRefreshFunction,
    resolve: (response: Response) => void,
}

class RefreshState {
    public isRefreshing = false
    public startRefreshing = () => {
        this.isRefreshing = true
    }
    public stopRefreshing = (authToken: string | null) => {
        this.isRefreshing = false
        this._runWaitingQueue(authToken)
    }
    private _waitingQueue: WaitingForRefreshItem[] = []
    private _runWaitingQueue = async (authToken: string | null) => {
        while (this._waitingQueue.length > 0) {
            const {waitingFunction, resolve} = this._waitingQueue.pop()!;
            waitingFunction(authToken).then(resolve)
        }
    }

    public waitForRefresh(waitingFunction: WaitingForRefreshFunction) {
        return new Promise<Response>((resolve, reject) => {
            this._waitingQueue.push({waitingFunction, resolve})
        })
    }
}
